/**
 * NaamDarbar — Admin Auth Context
 *
 * Stores a short-lived signed admin JWT in sessionStorage. The token is
 * issued by `POST /admin/auth/login` after email + password verification
 * and is sent on subsequent admin requests as `Authorization: Bearer ...`.
 *
 * Auto-logs the user out the moment the token's `expires_at` passes so a
 * leaked / stale token can never silently keep working in the UI.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { setAdminUnauthorizedHandler } from '../lib/api';

const SESSION_KEY = 'rd-admin-session';

// Base URL for admin API calls
const ADMIN_API = (import.meta.env.VITE_API_URL || '') + '/api/v1';

interface AdminSession {
  token: string;
  email: string;
  expiresAt: string; // ISO-8601
}

interface AdminContextValue {
  email: string;
  isAuthenticated: boolean;
  setSession: (s: AdminSession) => void;
  logout: (serverLogout?: boolean) => void;
  adminHeaders: () => Record<string, string>;
  /** Call on any 401 from an admin API call to clear session and redirect. */
  handle401: () => void;
}

const AdminContext = createContext<AdminContextValue | undefined>(undefined);

function readSession(): AdminSession | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as AdminSession;
    if (!s.token || !s.email || !s.expiresAt) return null;
    if (Date.parse(s.expiresAt) <= Date.now()) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return s;
  } catch {
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  }
}

export function AdminProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<AdminSession | null>(() => readSession());
  const expiryTimerRef = useRef<number | null>(null);

  const logout = useCallback((serverLogout = true) => {
    const currentSession = readSession();
    sessionStorage.removeItem(SESSION_KEY);
    setSessionState(null);
    // Best-effort server-side session revocation
    if (serverLogout && currentSession?.token) {
      fetch(`${ADMIN_API}/admin/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${currentSession.token}` },
      }).catch(() => { /* fire-and-forget */ });
    }
  }, []);

  const setSession = useCallback((s: AdminSession) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
    setSessionState(s);
  }, []);

  // Schedule an auto-logout exactly when the token expires.
  useEffect(() => {
    if (expiryTimerRef.current !== null) {
      window.clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (!session) return;
    const ms = Date.parse(session.expiresAt) - Date.now();
    if (ms <= 0) {
      logout();
      return;
    }
    expiryTimerRef.current = window.setTimeout(logout, ms);
    return () => {
      if (expiryTimerRef.current !== null) {
        window.clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [session, logout]);

  const adminHeaders = useCallback((): Record<string, string> => {
    return session ? { Authorization: `Bearer ${session.token}` } : {};
  }, [session]);

  const handle401 = useCallback(() => {
    // Server revoked session (idle timeout, disable, etc.) — clear locally without
    // trying to call logout (the token is already dead). Clearing the session
    // flips isAuthenticated to false; AdminLayout then redirects to /admin/login.
    logout(false);
  }, [logout]);

  // Wire the axios interceptor so any 401 from an /admin/* API call (other than
  // login) auto-logs-out instead of leaving a dead admin UI on screen.
  useEffect(() => {
    setAdminUnauthorizedHandler(handle401);
    return () => setAdminUnauthorizedHandler(null);
  }, [handle401]);

  const value = useMemo(
    () => ({
      email: session?.email ?? '',
      isAuthenticated: !!session,
      setSession,
      logout,
      adminHeaders,
      handle401,
    }),
    [session, setSession, logout, adminHeaders, handle401],
  );

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin(): AdminContextValue {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
}
